import {
  AbilityId,
  Actor,
  Collider,
  Dashing,
  Falling,
  Instability,
  PlayerTag,
  Static,
  Transform,
  Velocity,
  type CharacterKind,
} from '@/components'
import type { Entity } from '@/core/ecs/Entity'
import type { World } from '@/core/ecs/World'
import { EventsResource, MatchResource, worldScale } from '@/game/resources'
import { Events, type SignatureMove } from '@/game/events'
import { applyKnockback, fireVortex, fireWave } from './handlers'
import { spawnProp } from '@/game/prefabs'
import { launchSpeed, VORTEX, WAVE, type LaunchSpec } from './definitions'

/**
 * Announces what a move actually did, for presentation to draw.
 *
 * Every signature calls this. The alternative — presentation guessing from the
 * slot — is what produced an eight-metre shockwave ring for a two-metre scoop
 * and no visual at all for a teleport.
 */
function announce(
  world: World,
  caster: Entity,
  move: SignatureMove,
  radius: number,
  hit: boolean,
  toX?: number,
  toZ?: number,
): void {
  const tag = world.get(caster, PlayerTag)
  const tr = world.get(caster, Transform)
  const events = world.tryResource(EventsResource)
  if (!tag || !tr || !events) return
  events.emit(Events.Signature, {
    entity: caster,
    player: tag.player,
    move,
    x: tr.x,
    z: tr.z,
    angle: tr.angle,
    radius,
    toX: toX ?? tr.x,
    toZ: toZ ?? tr.z,
    hit,
  })
}

/**
 * Signature moves: the two slots where fighters genuinely differ.
 *
 * Everything before this was multipliers on the same five verbs, and the player
 * was right that it read as one character six times. The research behind the
 * split is in docs/ROSTER.md; the short version is that every game which solved
 * this keeps the defensive and mobility buttons universal — a fighter with no
 * escape is a trap, not a roster entry — and puts the identity in one or two
 * moves that are *theirs*.
 *
 * Slot Q and slot R are resolved per fighter here. The shared Push, Dash and
 * Brace stay exactly as they are.
 */

/** Half-cone in radians for the cone-shaped signatures. */
const CONE_HALF = 0.48

/** Everything the caster could hit, minus the caster and anything falling. */
function targetsInCone(
  world: World,
  caster: Entity,
  range: number,
  halfAngle: number,
): Array<{ entity: Entity; dirX: number; dirZ: number; dist: number }> {
  const tr = world.read(caster, Transform)
  const fx = Math.sin(tr.angle)
  const fz = Math.cos(tr.angle)
  const found: Array<{ entity: Entity; dirX: number; dirZ: number; dist: number }> = []

  for (const target of world
    .query([Transform, Velocity, Collider], { none: [Static, Falling] })
    .entities()) {
    if (target === caster) continue
    const t = world.read(target, Transform)
    const dx = t.x - tr.x
    const dz = t.z - tr.z
    const dist = Math.hypot(dx, dz)
    if (dist > range || dist < 1e-3) continue
    const nx = dx / dist
    const nz = dz / dist
    if (nx * fx + nz * fz < Math.cos(halfAngle)) continue
    found.push({ entity: target, dirX: nx, dirZ: nz, dist })
  }
  return found
}

function instabilityOf(world: World, e: Entity): number {
  return world.get(e, Instability)?.value ?? 0
}

// ── БРОНЯ ────────────────────────────────────────────────────────────────

/**
 * ЗАСЛОН — a slab of cover thrown down in front of the fighter.
 *
 * Soft rather than solid on purpose: at 3.4 m it would seal the bridge arena's
 * only crossing, which is 6 m wide. A walker gets through at a cost; a launched
 * body, a dash and a projectile do not. See docs/ROSTER.md.
 */
export function fireBarrier(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const x = tr.x + Math.sin(tr.angle) * 2.2
  const z = tr.z + Math.cos(tr.angle) * 2.2
  spawnProp(world, { kind: 'barrier', x, z, angle: tr.angle, scale: 2.6 })
  announce(world, caster, 'barrier', 2.6, true, x, z)
}

/**
 * ХОД — an armoured advance. Not a dash: he walks, and he will not be moved.
 *
 * The longest telegraph in the game (0.45 s of wind-up in the ability table)
 * paid for with near-immunity to knockback while it runs.
 */
export function fireCharge(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const actor = world.read(caster, Actor)
  let dx = actor.thrustX
  let dz = actor.thrustZ
  if (Math.hypot(dx, dz) < 0.2) {
    dx = Math.sin(tr.angle)
    dz = Math.cos(tr.angle)
  } else {
    const len = Math.hypot(dx, dz)
    dx /= len
    dz /= len
  }
  tr.angle = Math.atan2(dx, dz)
  world.add(caster, Dashing, {
    timeLeft: 1.2,
    dirX: dx,
    dirZ: dz,
    speed: 7.5,
    hits: [],
  })
  announce(world, caster, 'charge', 9, true)
}

// ── ХВАТ ───────────────────────────────────────────────────────────────────

/** Launch spec for the toss: little distance, a lot of air. */
const TOSS: LaunchSpec = { base: 14, scaling: 5 }

/**
 * ПОДБРОС — scoops the target into the air.
 *
 * Moves them barely at all and takes them out of the fight for most of a
 * second: a set-up, not a kill. Yasuo's ultimate is built the same way — it
 * requires an already-airborne target specifically so it cannot burst alone.
 */
export function fireToss(world: World, caster: Entity): void {
  const caught = targetsInCone(world, caster, 2.4, 1.22)
  announce(world, caster, 'toss', 2.4, caught.length > 0)
  for (const hit of caught) {
    applyKnockback(
      world,
      hit.entity,
      hit.dirX,
      hit.dirZ,
      launchSpeed(TOSS, instabilityOf(world, hit.entity), worldScale(world)),
      6,
      'push',
      caster,
      hit.entity === caster ? 0 : 0,
      0,
    )
    // The air is the point: an extra shove straight up, on top of the launch.
    const vel = world.get(hit.entity, Velocity)
    if (vel) vel.y = Math.max(vel.y, 11)
  }
}

/**
 * ЗАХВАТ (пока — рывок с добавкой) — a short lunge that plants the target.
 *
 * The real grab from docs/ROSTER.md needs a bound-entity state, a mash-out with
 * a floor, grab armour and a drawn immunity window; it is the one expensive
 * item on the list and it is not built yet. This is the honest interim: a very
 * short, very fast closing move with a heavy hit, so ХВАТ still plays like a
 * fighter who has to be on top of you.
 */
export function fireLunge(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  world.add(caster, Dashing, {
    timeLeft: 0.16,
    dirX: Math.sin(tr.angle),
    dirZ: Math.cos(tr.angle),
    speed: 21,
    hits: [],
  })
  announce(world, caster, 'lunge', 3.4, true)
}

// ── ЕГЕРЬ ───────────────────────────────────────────────────────────────────

/**
 * ВЕТЕР — a gust that shoves without taking control away.
 *
 * The windbox from Smash, with its two published rules kept intact: it does not
 * stun, and a stance stops it dead (`applyKnockback` handles both through
 * `flinchless`). It is an impulse on a cooldown rather than a held stream —
 * held flinchless push is the documented recipe for a broken mechanic.
 */
export function fireGust(world: World, caster: Entity): void {
  const caught = targetsInCone(world, caster, 6.5, CONE_HALF)
  announce(world, caster, 'gust', 6.5, caught.length > 0)
  for (const hit of caught) {
    // Falls off with distance: at the tip it is a nudge, up close it moves you.
    const falloff = 1 - (hit.dist / 6.5) * 0.55
    applyKnockback(
      world,
      hit.entity,
      hit.dirX,
      hit.dirZ,
      13 * falloff,
      0,
      'wave',
      caster,
      0,
      0,
      { flinchless: true },
    )
  }
}

/**
 * ОТРЫВ — a long hop backwards, away from whoever is closest.
 *
 * The zoner's second tool is not another way to hit: it is the way he keeps the
 * distance his first tool needs. Aimed away from the nearest fighter rather than
 * along his own facing, because he is looking at the person he is fleeing.
 */
export function fireRetreat(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  let awayX = -Math.sin(tr.angle)
  let awayZ = -Math.cos(tr.angle)
  let nearest = Infinity

  for (const other of world.query([Actor, Transform], { none: [Falling] }).entities()) {
    if (other === caster) continue
    const t = world.read(other, Transform)
    const dx = tr.x - t.x
    const dz = tr.z - t.z
    const dist = Math.hypot(dx, dz)
    if (dist >= nearest || dist < 1e-3) continue
    nearest = dist
    awayX = dx / dist
    awayZ = dz / dist
  }

  world.add(caster, Dashing, {
    timeLeft: 0.3,
    dirX: awayX,
    dirZ: awayZ,
    speed: 19,
    hits: [],
  })
  // Reported along the escape, not along his facing: the effect has to point
  // where the body is about to go, or it reads as a hit he did not make.
  announce(
    world,
    caster,
    'retreat',
    5.7,
    true,
    tr.x + awayX * 5.7,
    tr.z + awayZ * 5.7,
  )
}

// ── ЗНАХАРЬ ────────────────────────────────────────────────────────────────────

/**
 * ПОДМЕНА — swaps places with the nearest fighter, launch and all.
 *
 * The whole character: he does not kill, he hands somebody else's fatal flight
 * to somebody else. Instability stays with the body it belongs to — it is wear
 * on a person, not on a position.
 */
export function fireSwap(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  let target: Entity | null = null
  let nearest = 7.5

  for (const other of world.query([Actor, Transform], { none: [Falling] }).entities()) {
    if (other === caster) continue
    const t = world.read(other, Transform)
    const dist = Math.hypot(t.x - tr.x, t.z - tr.z)
    if (dist >= nearest) continue
    nearest = dist
    target = other
  }
  if (target === null) return

  const other = world.read(target, Transform)
  const x = tr.x
  const z = tr.z
  tr.x = other.x
  tr.z = other.z
  other.x = x
  other.z = z
  // Previous positions move too, or the interpolation draws both of them
  // streaking across the arena for a frame.
  tr.prevX = tr.x
  tr.prevZ = tr.z
  other.prevX = other.x
  other.prevZ = other.z

  // Both ends, because a teleport with one effect reads as a body vanishing.
  announce(world, caster, 'swap', 2.4, true, x, z)

  // The velocities swap with the bodies: taking somebody's place means taking
  // where they were going, which is the entire point when they were on their
  // way off the platform.
  const mine = world.get(caster, Velocity)
  const theirs = world.get(target, Velocity)
  if (mine && theirs) {
    const vx = mine.x
    const vz = mine.z
    const vy = mine.y
    mine.x = theirs.x
    mine.z = theirs.z
    mine.y = theirs.y
    theirs.x = vx
    theirs.z = vz
    theirs.y = vy
  }
}

// ── БРОДЯГА ───────────────────────────────────────────────────────────────────

/** ПРОКАТ — tucks in and rolls. Long, committed, and impossible to turn. */
export function fireRoll(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  world.add(caster, Dashing, {
    timeLeft: 0.85,
    dirX: Math.sin(tr.angle),
    dirZ: Math.cos(tr.angle),
    speed: 14,
    hits: [],
  })
  announce(world, caster, 'roll', 12, true)
}

// ── ПРОРАБ ─────────────────────────────────────────────────────────────────

/** ОБВАЛ — falls forward with his whole weight. Hit and you win the round. */
export function fireSlam(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  world.add(caster, Dashing, {
    timeLeft: 0.35,
    dirX: Math.sin(tr.angle),
    dirZ: Math.cos(tr.angle),
    speed: 14.8,
    hits: [],
  })
  fireWave(world, caster)
  announce(world, caster, 'slam', 5.2, true)
}

/** СБРОС — throws one of his own crates. It stays on the arena afterwards. */
export function fireCrate(world: World, caster: Entity): void {
  const tr = world.read(caster, Transform)
  const fx = Math.sin(tr.angle)
  const fz = Math.cos(tr.angle)
  const crate = spawnProp(world, {
    kind: 'crate',
    x: tr.x + fx * 1.6,
    z: tr.z + fz * 1.6,
    angle: tr.angle,
  })
  const vel = world.get(crate, Velocity)
  if (vel) {
    vel.x = fx * 26
    vel.z = fz * 26
  }
  announce(world, caster, 'crate', 1.6, true, tr.x + fx * 1.6, tr.z + fz * 1.6)
}

// ── the table ──────────────────────────────────────────────────────────────

type Fire = (world: World, caster: Entity) => void

/**
 * The two shared handlers, wrapped so they announce themselves like the rest.
 *
 * ВОЛНА and АНОМАЛИЯ survive as personal moves, and presentation now draws the
 * signature rather than the slot. Without these wrappers АНОМАЛИЯ — which sits
 * in ЗНАХАРЬ's *wave* slot — would be drawn with the shockwave's rings, and the
 * effect would describe a move he does not have.
 */
function fireWaveSignature(world: World, caster: Entity): void {
  fireWave(world, caster)
  announce(world, caster, 'wave', WAVE.maxRadius, true)
}

function fireVortexSignature(world: World, caster: Entity): void {
  fireVortex(world, caster)
  announce(world, caster, 'anomaly', VORTEX.radius, true)
}

/**
 * Which move each fighter has in the two signature slots.
 *
 * Slot Q is the old Wave button, slot R the old Vortex button. Both keep their
 * keys and their icons; what they *do* is now personal. Wave survives as БРОДЯГА's
 * signature and Vortex as ЗНАХАРЬ's — nothing that was built gets thrown away.
 */
export const SIGNATURES: Record<CharacterKind, Partial<Record<AbilityId, Fire>>> = {
  guard: { [AbilityId.Wave]: fireBarrier, [AbilityId.Vortex]: fireCharge },
  beetle: { [AbilityId.Wave]: fireToss, [AbilityId.Vortex]: fireLunge },
  crane: { [AbilityId.Wave]: fireGust, [AbilityId.Vortex]: fireRetreat },
  brick: { [AbilityId.Wave]: fireCrate, [AbilityId.Vortex]: fireSlam },
  jester: { [AbilityId.Wave]: fireVortexSignature, [AbilityId.Vortex]: fireSwap },
  blob: { [AbilityId.Wave]: fireWaveSignature, [AbilityId.Vortex]: fireRoll },
}

/**
 * What the HUD calls each signature.
 *
 * A move that is personal and a bar that names it "ВОЛНА" for everybody is worse
 * than no difference at all: the player reads the label, presses, gets something
 * else, and concludes the game is random. The bar has to say what this fighter's
 * button actually does.
 */
export const SIGNATURE_LABELS: Record<
  CharacterKind,
  Partial<Record<AbilityId, { name: string; description: string }>>
> = {
  guard: {
    [AbilityId.Wave]: { name: 'ЗАСЛОН', description: 'Ставит преграду перед собой' },
    [AbilityId.Vortex]: { name: 'ХОД', description: 'Идёт вперёд, сдвинуть нельзя' },
  },
  beetle: {
    [AbilityId.Wave]: { name: 'ПОДБРОС', description: 'Подкидывает вверх вплотную' },
    [AbilityId.Vortex]: { name: 'ЗАХВАТ', description: 'Короткий рывок в упор' },
  },
  crane: {
    [AbilityId.Wave]: { name: 'ВЕТЕР', description: 'Толкает издали, не оглушая' },
    [AbilityId.Vortex]: { name: 'ОТРЫВ', description: 'Прыжок прочь от ближнего' },
  },
  brick: {
    [AbilityId.Wave]: { name: 'СБРОС', description: 'Метает ящик, он остаётся' },
    [AbilityId.Vortex]: { name: 'ОБВАЛ', description: 'Падает вперёд всем весом' },
  },
  jester: {
    [AbilityId.Wave]: { name: 'АНОМАЛИЯ', description: 'Стягивает всех и рвёт' },
    [AbilityId.Vortex]: { name: 'ПОДМЕНА', description: 'Меняется местами и полётом' },
  },
  blob: {
    [AbilityId.Wave]: { name: 'ВОЛНА', description: 'Кольцо во все стороны' },
    [AbilityId.Vortex]: { name: 'ПРОКАТ', description: 'Долгий разгон без поворота' },
  },
}

/**
 * When a bot should press each signature.
 *
 * The AI used to have one hand-written rule for "wave" and one for "vortex",
 * both written when those buttons meant the same thing for everybody. With the
 * slots personal, those rules are wrong for four fighters out of six: ЕГЕРЬ's
 * ОТРЫВ fired at "wave range" is a bot jumping away from a fight it was
 * winning, and ЗНАХАРЬ's ПОДМЕНА fired the same way hands the opponent a free
 * position. The window belongs next to the move it describes.
 */
export interface SignatureUse {
  /** Only worth pressing with the opponent inside this, in metres. */
  maxDist: number
  /** …and no closer than this, for moves that need room to travel. */
  minDist?: number
  /** Wants the opponent already rattled or near their edge. */
  finisher?: boolean
  /** Endangers the caster too: only in the open. */
  needsRoom?: boolean
  /** Fires on the caster's own trouble rather than the opponent's. */
  panic?: boolean
}

export const SIGNATURE_USE: Record<
  CharacterKind,
  Partial<Record<AbilityId, SignatureUse>>
> = {
  guard: {
    // The slab is worth throwing down while they are still coming, not once
    // they have arrived: put behind an opponent already in your face it does
    // nothing but block your own retreat.
    [AbilityId.Wave]: { maxDist: 9, minDist: 2.5 },
    [AbilityId.Vortex]: { maxDist: 9, minDist: 2.5 },
  },
  beetle: {
    [AbilityId.Wave]: { maxDist: 2.6 },
    [AbilityId.Vortex]: { maxDist: 5, minDist: 1.8 },
  },
  crane: {
    [AbilityId.Wave]: { maxDist: 6, finisher: true },
    // The escape: pressed because *he* is in trouble, not because they are.
    [AbilityId.Vortex]: { maxDist: 3.4, panic: true },
  },
  brick: {
    [AbilityId.Wave]: { maxDist: 12, minDist: 3 },
    [AbilityId.Vortex]: { maxDist: 5 },
  },
  jester: {
    [AbilityId.Wave]: { maxDist: 4.5, needsRoom: true },
    // ПОДМЕНА is the panic button by construction: it is only ever a good idea
    // when the position he is giving away is worse than the one he is taking.
    [AbilityId.Vortex]: { maxDist: 7, panic: true },
  },
  blob: {
    [AbilityId.Wave]: { maxDist: 5.5, finisher: true },
    [AbilityId.Vortex]: { maxDist: 12, minDist: 4 },
  },
}

/** Whether the situation matches this signature's window. */
export function signatureWanted(
  use: SignatureUse | undefined,
  ctx: {
    dist: number
    selfEdge: number
    opponentEdge: number
    opponentInstability: number
  },
): boolean {
  if (!use) return false
  if (ctx.dist > use.maxDist) return false
  if (use.minDist !== undefined && ctx.dist < use.minDist) return false
  if (use.panic) return ctx.selfEdge < 4.5 || ctx.dist < 2.2
  if (use.needsRoom && ctx.selfEdge < 7) return false
  if (use.finisher && ctx.opponentEdge > 6 && ctx.opponentInstability < 45) return false
  return true
}

/** Which fighter a slot belongs to, or nothing outside a match. */
export function kindOf(world: World, e: Entity): CharacterKind | undefined {
  const tag = world.get(e, PlayerTag)
  const match = world.tryResource(MatchResource)
  if (!tag || !match) return undefined
  return match.models[tag.player]
}

/** The signature for this fighter's slot, or nothing if they use the default. */
export function signatureFor(
  world: World,
  caster: Entity,
  ability: number,
): Fire | undefined {
  const tag = world.get(caster, PlayerTag)
  const match = world.tryResource(MatchResource)
  if (!tag || !match) return undefined
  const kind = match.models[tag.player]
  if (!kind) return undefined
  return SIGNATURES[kind]?.[ability as AbilityId]
}
